import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Columns3,
  Globe,
  List as ListIcon,
  MessageSquare,
  PanelsTopLeft,
  Users,
  Compass,
  ShieldCheck,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { VisitorStatus } from '@prakash-software/shared';
import { Badge } from './ui/badge';
import { useVisitorsStore, type VisitorRow } from '../stores/visitors';
import { useSiteNames, siteBadgeStyle } from '../stores/sites';
import { useMonitorUi } from '../stores/monitor-ui';
import {
  newSessionId,
  useConversationsStore,
  type Conversation,
} from '../stores/conversations';
import { useAgentStore } from '../stores/agent';
import { useCapacity } from '../lib/capacity';
import { useIncomingCallsStore } from '../stores/incoming-calls';
import { useRecordingsStore } from '../stores/recordings';
import { usePresenceStore } from '../stores/presence';
import { formatDateTime } from '../lib/datetime';
import { isVisitorLive } from '../lib/visitor-live';
import {
  colorForVisitor,
  pseudonymForUuid,
  type VisitorId,
} from '@prakash-software/shared';
import { visitorDisplayName } from '../lib/visitor-name';
import { ChatHistory } from './ChatHistory';

// Lazy: MapLibre and its stylesheet are ~230KB gzipped, for a view most
// sessions never open. Loading it with the dashboard would tax every agent on
// every page load to serve the two layouts that actually plot anything.
const VisitorMap = lazy(() => import('./VisitorMap'));
import styles from './VisitorMonitor.module.css';

// Bucket order drives the default row grouping. Six buckets, top → bottom:
// 0. Live Chats     — agent currently handling a conversation, visitor live
// 1. Ongoing Chats  — chat in progress (AI- or agent-handled, has messages)
// 2. Engaged Users  — visitor in widget chat surface (no messages yet)
// 3. Browsing       — visitor on-site, widget closed
// 4. Returning      — returning visitor not currently active
// 5. Idle           — idle or departed
type SortKey =
  | 'default'
  | 'visitor'
  | 'site'
  | 'location'
  | 'onsite'
  | 'status'
  | 'engaged';
type SortDir = 'asc' | 'desc';

// ─── Board (kanban) view ───
// Columns are a PARTITION over the live-visitor rows using states we already
// derive — no new status is introduced. Each visitor lands in exactly ONE
// column via `assignColumn` precedence: internal traffic is segregated first
// (so team/office noise stays out of the real-visitor columns), then by chat
// activity. Column DISPLAY order is reorderable, but the assignment precedence
// is fixed and independent of it.
type BoardColumnId = 'visitors' | 'active' | 'idle' | 'internal';
interface BoardColumn {
  id: BoardColumnId;
  title: string;
  hint: string;
}
const BOARD_COLUMNS: Record<BoardColumnId, BoardColumn> = {
  visitors: {
    id: 'visitors',
    title: 'Visitors',
    hint: 'On-site, not in a chat',
  },
  active: {
    id: 'active',
    title: 'Active chats',
    hint: 'Visitor engaged in a conversation',
  },
  idle: {
    id: 'idle',
    title: 'Idle chats',
    hint: 'On-site but inactive',
  },
  internal: {
    id: 'internal',
    title: 'Internal traffic',
    hint: 'Connecting from an always-allowed (internal) IP',
  },
};

/** Which single column a visitor belongs to. Precedence, not display order. */
function assignColumn(r: VisitorRow): BoardColumnId {
  if (r.identity.internal) return 'internal';
  if (r.status === 'engaged') return 'active';
  if (r.status === 'idle') return 'idle';
  return 'visitors';
}

const DEFAULT_BOARD_ORDER: BoardColumnId[] = ['visitors', 'active', 'idle', 'internal'];
const BOARD_ORDER_KEY = 'inic.monitor.boardOrder';
const LAYOUT_KEY = 'inic.monitor.layout';

// ─── Live-monitor layouts ───
// `split` is the reason the map exists as a layout rather than as a separate
// tab: where a visitor is only means something next to who they are, and an
// agent deciding whether to reach out reads both. All four render the SAME
// filtered row set.
type Layout = 'list' | 'board' | 'split' | 'map';
const LAYOUTS: ReadonlyArray<{
  id: Layout;
  label: string;
  icon: typeof ListIcon;
}> = [
  { id: 'list', label: 'List view', icon: ListIcon },
  { id: 'board', label: 'Board view', icon: Columns3 },
  { id: 'split', label: 'Rows + map', icon: PanelsTopLeft },
  { id: 'map', label: 'Map view', icon: Globe },
];
const LAYOUT_IDS = new Set<string>(LAYOUTS.map((l) => l.id));

function loadLayout(): Layout {
  try {
    const saved = localStorage.getItem(LAYOUT_KEY);
    // Anything unrecognised — including the pre-split values — falls back to
    // the list rather than rendering nothing.
    return saved && LAYOUT_IDS.has(saved) ? (saved as Layout) : 'list';
  } catch {
    return 'list';
  }
}

/** Internal-traffic filter, matching Chat history's three-way control. */
type InternalFilter = 'all' | 'only' | 'hide';

function loadBoardOrder(): BoardColumnId[] {
  try {
    const raw = localStorage.getItem(BOARD_ORDER_KEY);
    if (!raw) return DEFAULT_BOARD_ORDER;
    const parsed = JSON.parse(raw) as BoardColumnId[];
    // Keep only known ids, then append any columns missing from storage so a
    // newly-added column can't be permanently hidden by a stale saved order.
    const known = parsed.filter((id) => id in BOARD_COLUMNS);
    for (const id of DEFAULT_BOARD_ORDER) if (!known.includes(id)) known.push(id);
    return known.length ? known : DEFAULT_BOARD_ORDER;
  } catch {
    return DEFAULT_BOARD_ORDER;
  }
}

export function VisitorMonitor() {
  // Read the stable Map reference, then derive the array — selectors that
  // return new arrays on every call cause infinite re-renders in Zustand v5.
  const rowsMap = useVisitorsStore((s) => s.rows);
  const rows = useMemo(() => Array.from(rowsMap.values()), [rowsMap]);
  // Site id → name map (stable ref; changes only when the fetch lands) for the
  // site-name labels + filter. Falls back to the origin host when unknown.
  const { byId: siteById } = useSiteNames();
  const siteLabelFor = useCallback(
    (r: VisitorRow) =>
      (r.siteId ? siteById.get(r.siteId)?.name : undefined) ??
      (r.context.origin ? originHost(r.context.origin) : ''),
    [siteById],
  );
  const tick = useTickEverySecond();
  // Tab (monitor vs history) lives in the monitor-ui store, NOT local state, so
  // opening a conversation and coming back restores the same tab + filters +
  // cached rows instead of resetting to the live monitor.
  const [searchParams] = useSearchParams();
  const mode = useMonitorUi((s) => s.mode);
  const setMode = useMonitorUi((s) => s.setMode);
  const patchHistoryQuery = useMonitorUi((s) => s.patchHistoryQuery);
  // Deep-link support: /dashboard?mode=history&q=<email> opens Chat history
  // pre-filtered (e.g. from the Contacts page "View conversations" button).
  // Applied once into the store so it composes with the persisted state; a plain
  // return from a conversation has no query params, so this is a no-op then.
  useEffect(() => {
    if (searchParams.get('mode') === 'history') setMode('history');
    const q = searchParams.get('q');
    if (q) patchHistoryQuery({ q });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Live monitor shows ONLY live/active visitors with a client-side search.
  // Past chats + agent/site/date/location filters live in the Chat history tab.
  const [q, setQ] = useState('');
  // Sort state for the live section (bucketed grouping by default).
  const [liveSortKey, setLiveSortKey] = useState<SortKey>('default');
  const [liveSortDir, setLiveSortDir] = useState<SortDir>('desc');
  // List / board / split / map layout for the live monitor. Persisted.
  const [layout, setLayout] = useState<Layout>(loadLayout);
  useEffect(() => {
    try {
      localStorage.setItem(LAYOUT_KEY, layout);
    } catch {
      /* private mode — non-fatal */
    }
  }, [layout]);
  // Live filters — the Chat-history filter bar, restricted to the fields that
  // exist for a visitor who is on site right now. Applied to the shared
  // liveRows so the list, the board and the map all honour them.
  const [siteFilter, setSiteFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [engagedOnly, setEngagedOnly] = useState(false);
  // Three-way, not the old checkbox: "internal only" is a real question (is
  // the team testing the widget?) that a hide/show toggle cannot ask, and it
  // is the control Chat history already offers.
  const [internalFilter, setInternalFilter] = useState<InternalFilter>('all');
  const makeColumnClick = useCallback(
    (
      currentKey: SortKey,
      setKey: (k: SortKey) => void,
      setDir: (updater: (d: SortDir) => SortDir) => void,
    ) =>
      (key: SortKey) => {
        if (currentKey === key) {
          setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
          setKey(key);
          const asc = key === 'visitor' || key === 'site' || key === 'location';
          setDir(() => (asc ? 'asc' : 'desc'));
        }
      },
    [],
  );
  const onLiveColumnClick = useMemo(
    () => makeColumnClick(liveSortKey, setLiveSortKey, setLiveSortDir),
    [liveSortKey, makeColumnClick],
  );
  function resetLiveSort() {
    setLiveSortKey('default');
    setLiveSortDir('desc');
  }

  // On-site right now — the base set the filters + options derive from.
  const allLive = useMemo(() => rows.filter((r) => isVisitorLive(r, tick)), [rows, tick]);

  // Filter-dropdown options, from the full live set so a choice never hides
  // itself. Sorted for stable ordering across the per-second tick.
  const siteOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of allLive) {
      const label = siteLabelFor(r);
      if (label) set.add(label);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [allLive, siteLabelFor]);
  const locationOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of allLive) if (r.identity.geo?.country) set.add(r.identity.geo.country);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [allLive]);

  // Live = on-site, narrowed by search + site + location + engaged. Shared base
  // for both the list and the board; the board facets this set into columns and
  // the list additionally applies hide-internal + sort.
  const liveRows = useMemo(() => {
    const search = q.trim().toLowerCase();
    return allLive.filter((r) => {
      if (siteFilter && siteLabelFor(r) !== siteFilter) return false;
      if (locationFilter && r.identity.geo?.country !== locationFilter) return false;
      // "Engaged" = actively in a chat or has exchanged a message.
      if (engagedOnly && !(r.status === 'engaged' || r.lastMessageAt != null)) return false;
      if (!search) return true;
      const hay = [
        r.identity.name ?? '',
        r.identity.visitorId,
        r.context.pagePath,
        r.identity.geo?.city ?? '',
        r.identity.geo?.country ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(search);
    });
  }, [allLive, q, siteFilter, locationFilter, engagedOnly, siteLabelFor]);

  const displayRows = useMemo(() => {
    const list =
      internalFilter === 'all'
        ? liveRows.slice()
        : liveRows.filter((r) => Boolean(r.identity.internal) === (internalFilter === 'only'));
    sortInPlace(list, liveSortKey, liveSortDir);
    return list;
  }, [liveRows, internalFilter, liveSortKey, liveSortDir]);

  const internalCount = useMemo(
    () => liveRows.filter((r) => r.identity.internal).length,
    [liveRows],
  );

  const stats = useMemo(() => {
    const live = allLive.length;
    let engaged = 0;
    let browsing = 0;
    let idle = 0;
    let internal = 0;
    for (const r of allLive) {
      if (r.identity.internal) internal++;
      if (r.status === 'engaged' || r.lastMessageAt != null) engaged++;
      else if (r.status === 'idle') idle++;
      else browsing++;
    }
    return { live, engaged, browsing, idle, internal };
  }, [allLive]);

  const hasFilters = Boolean(
    q || siteFilter || locationFilter || engagedOnly || internalFilter !== 'all',
  );

  function clearFilters(): void {
    setQ('');
    setSiteFilter('');
    setLocationFilter('');
    setEngagedOnly(false);
    setInternalFilter('all');
  }

  // The map hands back a row, not a session — opening it is the same action
  // clicking the row is, so it goes through the same helper.
  const navigate = useNavigate();
  const openRow = useCallback(
    (row: VisitorRow) => openChatWith(row, navigate),
    [navigate],
  );

  return (
    <section>
      <header className={styles.head}>
        <div className={styles.headTitle}>
          <p className={styles.kicker}>
            <span className={styles.kickerDot}>●</span>
            <span className={styles.kickerText}>
              {mode === 'history'
                ? 'CHAT ARCHIVE · SESSION INTELLIGENCE'
                : 'ACTIVE TELEMETRY · REAL-TIME VISITOR MATRIX'}
            </span>
          </p>
          <h1 className={styles.title}>{mode === 'history' ? 'Chat history' : 'Visitors'}</h1>
        </div>
        <div className={styles.headControls}>
          {mode === 'monitor' ? (
            <div className={styles.segmented} role="group" aria-label="Layout">
              {LAYOUTS.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={[
                      styles.segment,
                      styles.segmentIcon,
                      layout === option.id ? styles.segmentActive : '',
                    ].join(' ')}
                    onClick={() => setLayout(option.id)}
                    aria-pressed={layout === option.id}
                    aria-label={option.label}
                    title={option.label}
                  >
                    <Icon size={15} aria-hidden />
                  </button>
                );
              })}
            </div>
          ) : null}
          <div className={styles.segmented} role="group" aria-label="Monitor mode">
            <button
              type="button"
              className={[styles.segment, mode === 'monitor' ? styles.segmentActive : ''].join(' ')}
              onClick={() => setMode('monitor')}
              aria-pressed={mode === 'monitor'}
            >
              Live monitor
            </button>
            <button
              type="button"
              className={[styles.segment, mode === 'history' ? styles.segmentActive : ''].join(' ')}
              onClick={() => setMode('history')}
              aria-pressed={mode === 'history'}
            >
              Chat history
            </button>
          </div>
        </div>
      </header>

      {mode === 'monitor' && (
        <div className={styles.kpiGrid}>
          <div className={styles.kpiCard}>
            <div className={styles.kpiHeader}>
              <span className={styles.kpiTitle}>Active Visitors</span>
              <Badge variant="outline" className="text-[10px] uppercase font-semibold text-emerald-400 border-emerald-500/20 bg-emerald-500/10">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block mr-1.5 animate-pulse" />
                Live Now
              </Badge>
            </div>
            <div className={styles.kpiValueRow}>
              <span className={styles.kpiValue}>{stats.live}</span>
              <span className={styles.kpiSub}>real-time sessions</span>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={styles.kpiHeader}>
              <span className={styles.kpiTitle}>Engaged in Chat</span>
              <Badge variant="outline" className="text-[10px] uppercase font-semibold text-emerald-400 border-emerald-500/20 bg-emerald-500/10">
                <MessageSquare size={11} className="mr-1" />
                In Conversation
              </Badge>
            </div>
            <div className={styles.kpiValueRow}>
              <span className={styles.kpiValue}>{stats.engaged}</span>
              <span className={styles.kpiSub}>active conversations</span>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={styles.kpiHeader}>
              <span className={styles.kpiTitle}>Browsing Pages</span>
              <Badge variant="outline" className="text-[10px] uppercase font-semibold text-amber-400 border-amber-500/20 bg-amber-500/10">
                <Compass size={11} className="mr-1" />
                High Intent
              </Badge>
            </div>
            <div className={styles.kpiValueRow}>
              <span className={styles.kpiValue}>{stats.browsing}</span>
              <span className={styles.kpiSub}>on-site exploration</span>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={styles.kpiHeader}>
              <span className={styles.kpiTitle}>Internal Traffic</span>
              <Badge variant="outline" className="text-[10px] uppercase font-semibold text-zinc-300 border-zinc-700 bg-zinc-800/40">
                <ShieldCheck size={11} className="mr-1" />
                Whitelisted
              </Badge>
            </div>
            <div className={styles.kpiValueRow}>
              <span className={styles.kpiValue}>{stats.internal}</span>
              <span className={styles.kpiSub}>team & test IP</span>
            </div>
          </div>
        </div>
      )}

      {/* Live filters. Deliberately the SAME single-row control bar as Chat
          history — same field order, same labels, same widths — because the
          two tabs are one surface an agent flips between, and a control that
          moves or changes shape between them costs a re-read every time.
          Agent and the date range are absent for the only reason that would
          justify a difference: neither exists for a visitor who is on-site
          right now, so rendering them would be dead UI, not parity. */}
      {mode === 'monitor' ? (
        <div className={styles.filters}>
          <label className={styles.field}>
            <span className={styles.filterLabel}>Site</span>
            <select
              className={styles.select}
              value={siteFilter}
              onChange={(e) => setSiteFilter(e.target.value)}
            >
              <option value="">All sites</option>
              {siteOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.filterLabel}>Location</span>
            <select
              className={styles.select}
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <option value="">All locations</option>
              {locationOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className={`${styles.field} ${styles.fieldSearch}`}>
            <span className={styles.filterLabel}>Search (name, page, location)</span>
            <input
              className={styles.input}
              type="search"
              value={q}
              placeholder="Search the visitors on site now…"
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search live visitors"
            />
          </label>
          <label className={`${styles.field} ${styles.fieldCheck}`}>
            <input
              type="checkbox"
              checked={engagedOnly}
              onChange={(e) => setEngagedOnly(e.target.checked)}
            />
            <span className={styles.filterLabel}>Engaged only</span>
          </label>
          <label className={styles.field}>
            <span className={styles.filterLabel}>Internal</span>
            <select
              className={styles.select}
              value={internalFilter}
              onChange={(e) => setInternalFilter(e.target.value as InternalFilter)}
              title="Visitors connecting from an always-allowed (internal) IP"
            >
              <option value="all">All visitors</option>
              <option value="hide">Hide internal ({internalCount})</option>
              <option value="only">Internal only ({internalCount})</option>
            </select>
          </label>
          {hasFilters ? (
            <button type="button" className={styles.ghost} onClick={clearFilters}>
              Clear
            </button>
          ) : null}
        </div>
      ) : null}

      {mode === 'history' ? (
        <ChatHistory />
      ) : layout === 'board' ? (
        <VisitorBoard rows={displayRows} tick={tick} internalFilter={internalFilter} />
      ) : layout === 'map' ? (
        <div className={styles.listWrap}>
          <Suspense fallback={<p className={styles.empty}>Loading map…</p>}>
            <VisitorMap rows={displayRows} siteById={siteById} onOpen={openRow} />
          </Suspense>
        </div>
      ) : layout === 'split' ? (
        // Rows and map side by side over the SAME filtered set — the map is a
        // second reading of the list, not a second query, so a pin and a row
        // can never disagree about who is on site.
        <div className={`${styles.listWrap} ${styles.split}`}>
          <div className={styles.splitList}>
            <VisitorSection
              heading="Live now"
              subheading={`${displayRows.length} active`}
              rows={displayRows}
              tick={tick}
              sortKey={liveSortKey}
              sortDir={liveSortDir}
              onColumnClick={onLiveColumnClick}
              onReset={resetLiveSort}
              defaultKey="default"
              emptyHint="No live visitors right now."
            />
          </div>
          <div className={styles.splitMap}>
            <Suspense fallback={<p className={styles.empty}>Loading map…</p>}>
              <VisitorMap rows={displayRows} siteById={siteById} onOpen={openRow} />
            </Suspense>
          </div>
        </div>
      ) : (
        <div className={styles.listWrap}>
          <VisitorSection
            heading="Live now"
            subheading={`${displayRows.length} active`}
            rows={displayRows}
            tick={tick}
            sortKey={liveSortKey}
            sortDir={liveSortDir}
            onColumnClick={onLiveColumnClick}
            onReset={resetLiveSort}
            defaultKey="default"
            emptyHint="No live visitors right now."
          />
        </div>
      )}
    </section>
  );
}

// ─── Board (kanban) view — facet columns, horizontally scrollable, columns
// reorderable by drag. Cards reuse the same row helpers (openChatWith,
// liveness, unread) so nothing new is derived. ───
function VisitorBoard({
  rows,
  tick,
  internalFilter,
}: {
  rows: VisitorRow[];
  tick: number;
  internalFilter: InternalFilter;
}) {
  const [order, setOrder] = useState<BoardColumnId[]>(loadBoardOrder);
  const [dragId, setDragId] = useState<BoardColumnId | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(BOARD_ORDER_KEY, JSON.stringify(order));
    } catch {
      /* private mode — non-fatal */
    }
  }, [order]);

  // Partition rows into columns (each visitor in exactly one), newest-first
  // within each — matching the list's default sort.
  const buckets = useMemo(() => {
    const copy = rows.slice();
    sortInPlace(copy, 'default', 'desc');
    const map: Record<BoardColumnId, VisitorRow[]> = {
      visitors: [],
      active: [],
      idle: [],
      internal: [],
    };
    for (const r of copy) map[assignColumn(r)].push(r);
    return map;
  }, [rows]);

  function reorder(target: BoardColumnId) {
    if (!dragId || dragId === target) return;
    setOrder((prev) => {
      const next = prev.filter((id) => id !== dragId);
      const at = next.indexOf(target);
      next.splice(at, 0, dragId);
      return next;
    });
  }

  return (
    <div className={styles.board} role="list" aria-label="Visitor board">
      {order
        // The rows are already filtered; drop the column too, so "hide
        // internal" doesn't leave a permanently empty lane on the board.
        .filter((id) =>
          internalFilter === 'all' ? true : (id === 'internal') === (internalFilter === 'only'),
        )
        .map((id) => {
        const col = BOARD_COLUMNS[id];
        const cards = buckets[id];
        return (
          <section
            key={id}
            className={[styles.boardCol, dragId === id ? styles.boardColDragging : ''].join(' ')}
            role="listitem"
            onDragOver={(e) => {
              e.preventDefault();
              reorder(id);
            }}
          >
            <header
              className={styles.boardColHead}
              draggable
              onDragStart={() => setDragId(id)}
              onDragEnd={() => setDragId(null)}
              title="Drag to reorder columns"
            >
              <span className={styles.boardColTitle}>{col.title}</span>
              <span className={styles.boardColCount}>{cards.length}</span>
            </header>
            <div className={styles.boardColBody}>
              {cards.length === 0 ? (
                <p className={styles.boardEmpty}>{col.hint}</p>
              ) : (
                cards.map((r) => <VisitorCard key={r.identity.visitorId} row={r} now={tick} />)
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function VisitorCard({ row, now }: { row: VisitorRow; now: number }) {
  const navigate = useNavigate();
  const isActive = isVisitorLive(row, now);
  const name = visitorDisplayName(row.identity.visitorId, row.identity.name);
  const { byId: siteById } = useSiteNames();
  const site = row.siteId ? siteById.get(row.siteId) : undefined;
  const siteName = site?.name ?? null;
  // Same notations the list row carries — starred + recording resolve from the
  // stores exactly as Row does.
  const isStarred = useConversationsStore((s) => {
    for (const c of s.byId.values()) {
      if (c.visitorId === row.identity.visitorId && c.starred) return true;
    }
    return false;
  });
  const hasRecording = useRecordingsStore((s) => {
    for (const c of useConversationsStore.getState().byId.values()) {
      if (c.visitorId !== row.identity.visitorId) continue;
      if (s.sessionIds.has(c.sessionId)) return true;
    }
    return false;
  });
  return (
    <button
      type="button"
      className={[styles.card, row.unread > 0 ? styles.cardUnread : ''].join(' ')}
      onClick={() => openChatWith(row, navigate)}
      title="Open chat"
    >
      <span className={styles.cardTop}>
        <span className={styles.cardAvatar} style={{ background: colorForVisitor(row.identity.visitorId) }}>
          {getInitials(name)}
        </span>
        <span className={styles.cardName}>
          {isStarred ? (
            <span className={styles.starFlag} title="Starred" aria-label="Starred">★</span>
          ) : null}
          {name}
        </span>
        {row.unread > 0 ? (
          <span className={styles.cardUnreadDot} aria-label={`${row.unread} new`}>
            {row.unread}
          </span>
        ) : null}
      </span>
      <span className={styles.cardMeta}>
        <span className={styles.cardStatus} data-status={isActive ? row.status : 'departed'}>
          {isActive ? row.status : 'offline'}
        </span>
        {isActive ? (
          <span className={styles.liveTag} title="On-site right now">LIVE</span>
        ) : null}
        {row.identity.isReturning ? (
          <span
            className={styles.returningFlag}
            title={row.context.previousChatSummary ?? 'Returning visitor (no summary on file)'}
          >
            ↩
          </span>
        ) : null}
        {hasRecording ? (
          <span className={styles.recIndicator} title="Has a recorded session" aria-label="Has recording">●</span>
        ) : null}
        {row.identity.internal ? <span className={styles.cardInternal}>INTERNAL</span> : null}
      </span>
      <span className={styles.cardSite}>
        <span className={styles.siteBadge} title={siteName ?? undefined}>
          {siteName ?? originHost(row.context.origin)}
        </span>
        {row.context.pagePath}
      </span>
    </button>
  );
}

// ─── Section component — one table, its own sort state ───
function VisitorSection({
  heading,
  subheading,
  rows,
  tick,
  sortKey,
  sortDir,
  onColumnClick,
  onReset,
  defaultKey,
  emptyHint,
}: {
  heading: string;
  subheading: string;
  rows: VisitorRow[];
  tick: number;
  sortKey: SortKey;
  sortDir: SortDir;
  onColumnClick: (k: SortKey) => void;
  onReset: () => void;
  defaultKey: SortKey;
  emptyHint: string;
}) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>{heading}</h2>
        <span className={styles.sectionMeta}>{subheading}</span>
      </div>
      <div className={styles.tableWrap} role="region" aria-label={heading}>
        {rows.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIconWrap}>
              <Users size={24} />
            </div>
            <h3 className={styles.emptyTitle}>No live visitors on-site</h3>
            <p className={styles.emptyDescription}>{emptyHint}</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <SortHeader label="Visitor" k="visitor" sortKey={sortKey} sortDir={sortDir} onClick={onColumnClick} />
                <SortHeader label="Site / page" k="site" sortKey={sortKey} sortDir={sortDir} onClick={onColumnClick} />
                <SortHeader label="Location" k="location" sortKey={sortKey} sortDir={sortDir} onClick={onColumnClick} />
                <SortHeader label="On site" k="onsite" sortKey={sortKey} sortDir={sortDir} onClick={onColumnClick} className={styles.numCol} />
                <SortHeader label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onClick={onColumnClick} />
                <SortHeader label="Engaged with" k="engaged" sortKey={sortKey} sortDir={sortDir} onClick={onColumnClick} />
                <th aria-label="Actions" className={styles.actionsCol}>
                  {sortKey !== defaultKey ? (
                    <button
                      type="button"
                      className={styles.resetSort}
                      onClick={onReset}
                      title="Reset to default sort for this section"
                    >
                      reset
                    </button>
                  ) : null}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Row key={r.identity.visitorId} row={r} now={tick} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Row({ row, now }: { row: VisitorRow; now: number }) {
  const navigate = useNavigate();
  const capacity = useCapacity();
  const { byId: siteById } = useSiteNames();
  const site = row.siteId ? siteById.get(row.siteId) : undefined;
  const siteName = site?.name ?? null;
  // Pulse the row if this visitor has a pending video invite for us.
  const ringingFor = useIncomingCallsStore((s) =>
    [...s.calls.values()].some((c) => c.from === row.identity.visitorId),
  );
  // Recording indicator: any session this visitor has on the server?
  const hasRecording = useRecordingsStore((s) => {
    for (const c of useConversationsStore.getState().byId.values()) {
      if (c.visitorId !== row.identity.visitorId) continue;
      if (s.sessionIds.has(c.sessionId)) return true;
    }
    return false;
  });
  // Starred = any conversation with this visitor carries the shared star.
  const isStarred = useConversationsStore((s) => {
    for (const c of s.byId.values()) {
      if (c.visitorId === row.identity.visitorId && c.starred) return true;
    }
    return false;
  });
  const onChat = () => openChatWith(row, navigate);
  const initials = getInitials(visitorDisplayName(row.identity.visitorId, row.identity.name));
  // Live = recent heartbeat & not departed. Status alone is too volatile:
  // an idle visitor (60s no input) is still reachable; a brief widget blip
  // shouldn't blank the row. See lib/visitor-live.ts for rationale.
  const isActive = isVisitorLive(row, now);
  // "Offline" is the right label for any visitor we can't reach right now:
  // they explicitly left (status === 'departed'), OR their last heartbeat
  // is older than the LIVE_GRACE window so we can't tell. Treating
  // anything but live as offline keeps the IDLE/BROWSING/ENGAGED labels
  // honest — those should only show when the visitor really is on-site.
  const isOffline = !isActive;
  const onSite = isActive ? formatDuration(Math.max(0, now - row.arrivedAt)) : '—';
  // Offline visitors keep their Chat button enabled — the agent may want to
  // open the conversation to read history, attachments, notes, summaries.
  // The compose area inside the conversation is disabled instead. Video,
  // by definition, requires the visitor to be present, so it stays disabled.
  const chatDisabled = !!row.lock || !capacity.canTakeChat;
  const chatTitle = isOffline
    ? 'Open past conversation (read-only while visitor is offline)'
    : row.lock
      ? `${row.lock.agentName} is engaging this visitor`
      : !capacity.canTakeChat
        ? capacity.videoStatus === 'on_call'
          ? 'You are on a video call'
          : 'Set yourself to Available first'
        : 'Start a chat with this visitor';

  // Whole row navigates to the conversation. Skip when the click started
  // inside an interactive control (the Chat/Video buttons handle their own
  // actions) so we don't double-fire — and skip when the user is selecting
  // text so they can copy a visitor id without bouncing pages.
  function onRowClick(e: React.MouseEvent<HTMLTableRowElement>) {
    const target = e.target as HTMLElement;
    if (target.closest('button, a, input, textarea, select')) return;
    if (window.getSelection()?.toString()) return;
    onChat();
  }

  return (
    <tr
      className={[
        styles.row,
        styles.rowClickable,
        row.lock ? styles.rowLocked : '',
        row.unread > 0 ? styles.rowUnread : '',
        ringingFor ? styles.rowRinging : '',
      ].join(' ')}
      // Mouse convenience only. No role="button" / tabIndex: that turned the
      // table row into a button containing buttons, which hides the row's
      // cells from screen readers. Keyboard users have the row's Chat button.
      onClick={onRowClick}
    >
      <td className={styles.visitor}>
        <span
          className={styles.avatar}
          aria-hidden
          style={{ background: colorForVisitor(row.identity.visitorId) }}
        >
          {row.unread > 0 ? (
            <span className={styles.unreadDot} aria-hidden />
          ) : null}
          {initials}
        </span>
        <div className={styles.visitorBody}>
          <div className={styles.visitorName}>
            {isStarred ? (
              <span className={styles.starFlag} title="Starred" aria-label="Starred">
                ★
              </span>
            ) : null}
            {/* Wrapped so it is the NAME that truncates, not the flags after
                it. A bare text node in a flex row cannot be given an ellipsis,
                so a long pseudonym overflowed the cell and ran underneath the
                next column's site badge. */}
            <span className={styles.visitorNameText}>
              {visitorDisplayName(row.identity.visitorId, row.identity.name)}
            </span>
            {row.unread > 0 ? (
              // New-activity indicator. Sits right beside the name so the agent
              // notices a fresh inbound message WITHOUT the row moving. Set in
              // the visitors store (`noteIncoming`, visitor messages only) and
              // cleared when the agent opens the conversation (`markRead`).
              <span
                className={styles.newPill}
                title={`${row.unread} new message${row.unread > 1 ? 's' : ''}`}
                aria-label={`${row.unread} new message${row.unread > 1 ? 's' : ''}`}
              >
                {row.unread > 1 ? `${row.unread} new` : 'NEW'}
              </span>
            ) : null}
            {isActive ? (
              <span className={styles.liveTag} title="On-site right now">
                LIVE
              </span>
            ) : null}
            {hasRecording ? (
              <span
                className={styles.recIndicator}
                title="This visitor has a recorded session"
                aria-label="Has recording"
              >
                ●
              </span>
            ) : null}
            {row.identity.isReturning ? (
              <span
                className={styles.returningFlag}
                title={
                  row.context.previousChatSummary ??
                  'Returning visitor (no summary on file)'
                }
              >
                ↩
              </span>
            ) : null}
          </div>
          <div className={styles.visitorId}>
            {row.lastMessagePreview ? (
              <>
                <span
                  className={styles.previewIcon}
                  aria-hidden
                  title={
                    row.lastMessageAuthor === 'visitor'
                      ? 'Last: visitor message'
                      : row.lastMessageAuthor === 'ai'
                        ? 'Last: AI reply'
                        : 'Last: agent reply'
                  }
                >
                  {row.lastMessageAuthor === 'ai'
                    ? '🤖'
                    : row.lastMessageAuthor === 'agent'
                      ? '↩'
                      : '💬'}
                </span>
                {`"${row.lastMessagePreview.slice(0, 20)}${row.lastMessagePreview.length > 64 ? '…' : ''}"`}
              </>
            ) : row.identity.name ? (
              // Named visitor: show the stable pseudonym as a secondary handle.
              // Anonymous visitors already show it as the primary name above.
              pseudonymForUuid(row.identity.visitorId).display
            ) : null}
          </div>
          {row.identity.internal ? (
            <div className={styles.internalRow}>
              <span
                className={styles.internalTag}
                title="Connecting from an internal (whitelisted) IP"
              >
                INTERNAL
              </span>
            </div>
          ) : null}
        </div>
      </td>
      <td>
        <div className={styles.site}>
          <span
            className={styles.siteBadge}
            style={siteBadgeStyle(site)}
            title={siteName ?? undefined}
          >
            {siteName ?? originHost(row.context.origin)}
          </span>
        </div>
        <div className={styles.page}>{row.context.pagePath}</div>
      </td>
      <td>
        {row.identity.geo
          ? [row.identity.geo.city, row.identity.geo.country].filter(Boolean).join(', ')
          : '—'}
      </td>
      <td className={styles.num}>
        <span title={`Arrived ${formatDateTime(row.arrivedAt)}\nLast seen ${formatDateTime(row.seenAt)}`}>
          {onSite}
        </span>
        <div className={styles.timestampMeta} aria-hidden>
          {formatDateTime(row.arrivedAt)}
        </div>
      </td>
      <td>
        <span
          className={styles.statusPill}
          data-status={isOffline ? 'departed' : row.status}
        >
          {isOffline ? 'offline' : row.status}
        </span>
        {row.lock ? (
          <span className={styles.lockNote}>
            <span className={styles.lockAvatar} aria-hidden>
              {row.lock.agentName.slice(0, 2).toUpperCase()}
            </span>
            {row.lock.agentName} engaging…
          </span>
        ) : null}
      </td>
      <EngagementCell visitorId={row.identity.visitorId} />
      <td>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.action}
            disabled={chatDisabled}
            title={chatTitle}
            onClick={onChat}
            aria-label="Chat"
          >
            <MessageSquare size={14} aria-hidden />
            <span>Chat</span>
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── Helpers ───

function openChatWith(row: VisitorRow, navigate: (to: string) => void) {
  const me = useAgentStore.getState().current;
  if (!me) return;
  const existing = findVisitorConversation(row.identity.visitorId);
  const sessionId = existing?.sessionId ?? newSessionId();
  const store = useConversationsStore.getState();
  // Opening a conversation is OBSERVATION, not takeover. If the AI is
  // currently handling the visitor, we leave `ownedBy: 'ai'` alone — the
  // conversation header will show "Take over from AI" and the agent claims
  // explicitly (button, or by sending a message — server auto-promotes on
  // first agent message).
  if (!existing) {
    // True cold chat: visitor hasn't said anything; agent is initiating.
    // Opening the row is OBSERVATION — assignment is DEFERRED to the agent's
    // first message (see Conversation.sendMessage), so we don't claim
    // ownership (no ownerAgentId → no ENGAGED pill on the monitor) and don't
    // emit session:start yet. `ownedBy: 'agent'` is the local draft state so
    // the composer renders "you are handling"; the seat isn't actually taken
    // until a message is sent.
    store.open({
      sessionId,
      visitorId: row.identity.visitorId,
      visitorName: visitorDisplayName(row.identity.visitorId, row.identity.name),
      medium: 'chat',
      startedBy: 'agent',
      startedAt: Date.now(),
      messages: [],
      notes: '',
      notesSaveState: 'idle',
      notesUpdatedAt: null,
      summary: null,
      summaryProvider: null,
      summaryGeneratedAt: null,
      callTranscripts: new Map(),
      inVideo: false,
      ownedBy: 'agent',
    });
  }
  navigate(`/conversation/${sessionId}`);
}

function findVisitorConversation(visitorId: VisitorRow['identity']['visitorId']):
  | Conversation
  | undefined {
  for (const c of useConversationsStore.getState().byId.values()) {
    if (c.visitorId === visitorId) return c;
  }
  return undefined;
}

function useTickEverySecond(): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

function originHost(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return origin;
  }
}

function formatDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function sortInPlace(arr: VisitorRow[], key: SortKey, dir: SortDir): void {
  if (key === 'default') {
    // STABLE arrival order: newest visitor first, and that position holds for
    // as long as the visitor stays continuously present. `arrivedAt` is stamped
    // when a visitor (re)appears online and then preserved across every
    // heartbeat / message / status-label change (see upsertFromPresence in
    // bus-bridge.ts), so ongoing activity never reshuffles the list — a
    // visitor only jumps back to the top if they went offline and returned
    // (a genuinely fresh arrival). We deliberately DON'T re-bucket or tiebreak
    // on lastMessageAt/seenAt here — that was the old behaviour that made rows
    // jump around on every update. New activity is surfaced in-place via the
    // per-row "NEW" indicator instead of a reorder. visitorId is the final
    // tiebreak so identical-arrival rows stay stable.
    arr.sort((a, b) => {
      if (b.arrivedAt !== a.arrivedAt) return b.arrivedAt - a.arrivedAt;
      return a.identity.visitorId < b.identity.visitorId ? -1 : 1;
    });
    return;
  }
  const m = dir === 'asc' ? 1 : -1;
  arr.sort((a, b) => m * compareByKey(key, a, b));
}

function compareByKey(key: SortKey, a: VisitorRow, b: VisitorRow): number {
  switch (key) {
    case 'visitor': {
      const an = visitorDisplayName(a.identity.visitorId, a.identity.name);
      const bn = visitorDisplayName(b.identity.visitorId, b.identity.name);
      return an.localeCompare(bn, undefined, { sensitivity: 'base' });
    }
    case 'site': {
      const as = originHost(a.context.origin) + a.context.pagePath;
      const bs = originHost(b.context.origin) + b.context.pagePath;
      return as.localeCompare(bs, undefined, { sensitivity: 'base' });
    }
    case 'location': {
      const al = [a.identity.geo?.country, a.identity.geo?.city].filter(Boolean).join(' ');
      const bl = [b.identity.geo?.country, b.identity.geo?.city].filter(Boolean).join(' ');
      return al.localeCompare(bl, undefined, { sensitivity: 'base' });
    }
    case 'onsite':
      return (a.arrivedAt ?? 0) - (b.arrivedAt ?? 0);
    case 'status':
      return statusRank(a.status) - statusRank(b.status);
    case 'engaged': {
      // Rows with an active engagement first; ties broken by recency.
      const ar = (a.lock ? 0 : 1) * 10 + (a.lastMessageAt ? 0 : 1);
      const br = (b.lock ? 0 : 1) * 10 + (b.lastMessageAt ? 0 : 1);
      return ar - br;
    }
    default:
      return 0;
  }
}

function statusRank(s: VisitorStatus): number {
  switch (s) {
    case 'engaged':
      return 0;
    case 'browsing':
      return 1;
    case 'idle':
      return 2;
    case 'departed':
      return 3;
  }
}

function SortHeader({
  label,
  k,
  sortKey,
  sortDir,
  onClick,
  className,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onClick: (k: SortKey) => void;
  className?: string;
}) {
  const active = sortKey === k;
  const arrow = active ? (sortDir === 'asc' ? '↑' : '↓') : '';
  return (
    <th className={className} aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        className={[styles.sortBtn, active ? styles.sortBtnActive : ''].join(' ')}
        onClick={() => onClick(k)}
        title={`Sort by ${label.toLowerCase()}`}
      >
        {label}
        <span className={styles.sortArrow} aria-hidden>{arrow}</span>
      </button>
    </th>
  );
}

function EngagementCell({ visitorId }: { visitorId: VisitorId }) {
  // Two PRIMITIVE selectors instead of one object — Zustand's default
  // Object.is equality on an object literal would re-render every tick and
  // trip React's getSnapshot caching check (infinite loop).
  const ownerAgentId = useConversationsStore((s) => {
    for (const c of s.byId.values()) {
      if (c.visitorId === visitorId && c.ownedBy === 'agent') {
        return c.ownerAgentId ?? null;
      }
    }
    return null;
  });
  // Persisted owner name from the server — resolves the handling agent even
  // when they're OFFLINE (live presence below only covers online agents).
  const ownerNamePersisted = useConversationsStore((s) => {
    for (const c of s.byId.values()) {
      if (c.visitorId === visitorId && c.ownedBy === 'agent') {
        return c.ownerAgentName ?? null;
      }
    }
    return null;
  });
  const inVideo = useConversationsStore((s) => {
    for (const c of s.byId.values()) {
      if (c.visitorId === visitorId && c.ownedBy === 'agent') {
        return c.inVideo;
      }
    }
    return false;
  });
  const me = useAgentStore((s) => s.current);
  const otherName = usePresenceStore((s) =>
    ownerAgentId ? s.others.get(ownerAgentId)?.name ?? null : null,
  );

  if (!ownerAgentId) {
    return (
      <td>
        <span className={styles.engagedIdle}>—</span>
      </td>
    );
  }
  const ownerName =
    me && ownerAgentId === me.agentId
      ? 'you'
      : otherName ?? ownerNamePersisted ?? `agent-${ownerAgentId.slice(0, 6)}`;
  const medium = inVideo ? 'video' : 'chat';
  return (
    <td>
      <span className={styles.engagedPill} data-medium={medium}>
        {medium} · {ownerName}
      </span>
    </td>
  );
}
